import styled from 'styled-components'
import { Col, Row } from 'react-styled-flexboxgrid'
import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react'
import media from 'css-in-js-media'
import {
    motion,
    useSpring,
    useTransform,
    useViewportScroll,
} from 'framer-motion'
import { Variables } from '../styles/Variables'
import { BodyCopy, H2, H5 } from './Typography'
import BlockText, { StyledFrame } from './BlockText'
import useCurrentWidth from './windowSize'
import { physics } from './SmoothScroll'

const LogoSrc = require('../svg/logo.svg') as string
const PazolaLandScape2xSrc = require('../svg/BannerHero2x.jpg') as string
const LandscapeMobile = require('../svg/BannerHero_mobile2x.jpg') as string

const ContentContainer = styled(Col)`
    text-align: center;
    padding-top: 146px;
    position: relative;
    z-index: 10;

    ${media('<desktop')} {
        padding: 148px 20px 0 20px;
        height: 100%;
        min-height: 667px;
        background-position: center;
        background-image: url(${LandscapeMobile});
        background-size: cover;
    }
`

const FullPage = styled(Row)`
    height: 200vh;
    min-height: 900px;
    position: relative;
    overflow: hidden;

    ${media('<desktop')} {
        height: 100%;
        min-height: 667px;
    }

    ${StyledFrame} {
        ${media('>desktop')} {
            min-height: unset;
        }
    }
`

const AnimatedLogoImage = styled(motion.img)`
    padding-bottom: 37px;
    max-width: 907px;

    ${media('<desktop')} {
        padding-bottom: 14px;
        max-width: 100%;
    }
`

const AnimatedSubtitle = styled(motion.div)`
    ${media('<desktop')} {
        svg {
            width: 4px;
            height: 4px;
        }
    }
`

const BannerImage = styled(motion.div)`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 0;
    background-image: url(${PazolaLandScape2xSrc});
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center center;
    height: 100vh;
    will-change: transform;
    transform: none;

    img {
        display: none;
    }

    ${media('<desktop')} {
        transform: none !important;
        background-image: none;
    }
`

const StyledH5 = styled(H5)`
    font-family: ${Variables.font.bodyBold};
    margin-bottom: 0;
`

const AnimateAway = styled.div``
const AnimatedDrawer = styled(motion.div)`
    ${media('>desktop')} {
        position: absolute;
        top: 0;
        right: 0;
        left: 50%;
        bottom: 0;
        z-index: 11;
        transform: translateX(100%) translateY(0) translateZ(0px);
        will-change: transform;
    }
    ${media('<desktop')} {
        transform: none !important;
    }
`

const Hero = ({ title, description }) => {
    const { scrollY } = useViewportScroll()
    const elementRef = useRef(null)
    const getPositionStart = 0
    const [getPositionEnd, setPositionEnd] = useState(1000)

    useEffect(() => {
        if (elementRef.current !== null)
            setPositionEnd(elementRef.current.offsetHeight)
    }, [elementRef])

    const updatePositions = useCallback((entries) => {
        for (let entry of entries) {
            setPositionEnd(entry.target.offsetHeight)
        }
    }, [])

    useLayoutEffect(() => {
        const resizeObserver = new ResizeObserver((entries) => {
            updatePositions(entries)
        })
        elementRef && resizeObserver.observe(elementRef.current)
        return () => resizeObserver.disconnect()
    }, [elementRef])

    const backgroundY = useSpring(
        useTransform(
            scrollY,
            [getPositionStart, getPositionEnd / 2, getPositionEnd / 2 + 1],
            [0, getPositionEnd / 2, getPositionEnd / 2],
            {
                clamp: false,
            }
        ),
        physics
    )

    const backgroundWidth = useSpring(
        useTransform(
            scrollY,
            [getPositionStart, getPositionEnd / 3],
            [0, -(useCurrentWidth() / 4)]
        ),
        physics
    )

    const drawerY = useSpring(
        useTransform(
            scrollY,
            [getPositionStart, getPositionEnd / 2, getPositionEnd / 2 + 1],
            [0, getPositionEnd / 2, getPositionEnd / 2],
            {
                clamp: false,
            }
        ),
        physics
    )

    const drawerX = useTransform(
        useSpring(
            useTransform(
                scrollY,
                [getPositionStart, getPositionEnd / 3],
                [100, 0]
            ),
            physics
        ),
        (value) => `${value}%`
    )

    const logoAnimation = {
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                type: 'spring',
                bounce: 0,
                duration: 1.5,
            },
        },
        hidden: { opacity: 0, y: 80 },
    }

    const subtitleAnimation = {
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                delay: 0.3,
                type: 'spring',
                bounce: 0,
                duration: 1.5,
            },
        },
        hidden: { opacity: 0, y: 70 },
    }

    return (
        <>
            <FullPage ref={elementRef}>
                <BannerImage style={{ y: backgroundY, x: backgroundWidth }}>
                    <img src={PazolaLandScape2xSrc} alt="" />
                </BannerImage>
                <ContentContainer xs={12} as={motion.div}>
                    <AnimateAway>
                        <AnimatedLogoImage
                            key="logo"
                            initial="hidden"
                            animate="visible"
                            variants={logoAnimation}
                            src={LogoSrc}
                            alt="Pazola"
                        />
                        <AnimatedSubtitle
                            key="subtitle"
                            initial="hidden"
                            animate="visible"
                            variants={subtitleAnimation}
                        >
                            <Row center="xs" middle="md">
                                <Col>
                                    <StyledH5>ANDERMATT</StyledH5>
                                </Col>
                                <Col>
                                    <svg
                                        width="6"
                                        height="6"
                                        viewBox="0 0 6 6"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <circle
                                            cx="3"
                                            cy="3"
                                            r="3"
                                            fill="#14323C"
                                        />
                                    </svg>
                                </Col>
                                <Col>
                                    <StyledH5>1444M</StyledH5>
                                </Col>
                                <Col>
                                    <svg
                                        width="6"
                                        height="6"
                                        viewBox="0 0 6 6"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <circle
                                            cx="3"
                                            cy="3"
                                            r="3"
                                            fill="#14323C"
                                        />
                                    </svg>
                                </Col>
                                <Col>
                                    <StyledH5>SWITZERLAND</StyledH5>
                                </Col>
                            </Row>
                        </AnimatedSubtitle>
                    </AnimateAway>
                </ContentContainer>
                <AnimatedDrawer
                    style={{ x: drawerX, y: drawerY }}
                    initial={{ x: '100%' }}
                >
                    <BlockText
                        mobileWidth={12}
                        width={12}
                        bgColor={Variables.color.lightGrey}
                        textAlign="left"
                        alignItems="end"
                        data={
                            <>
                                <BodyCopy
                                    dangerouslySetInnerHTML={{
                                        __html: title,
                                    }}
                                />
                                <H2
                                    dangerouslySetInnerHTML={{
                                        __html: description.replace(
                                            /h2>/g,
                                            'p>'
                                        ),
                                    }}
                                />
                            </>
                        }
                    />
                </AnimatedDrawer>
            </FullPage>
        </>
    )
}

export default Hero
